import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ApiError, initials } from '@limuzin/kit';
import type { MessageView } from '@limuzin/passenger';
import { deleteMessages, getChatAttachment, getMessages, markRead } from '../api';
import { askToNotify, chatTag } from '../notify';
import { discard, onDelivered, retry, send, usePending, type OutboxMessage } from '../outbox';
import { useAvatar, useSession } from '../session';
import { Icon } from '../material';
import { Initials, Spinner, useBack, useErrorHaptic } from '../widgets';
import {
  Bubble, ChatGallery, ChatHistory, ChatLayout, Composer, DeleteMessagesSheet, FailedSheet,
  OutboxBubble, Selectable, outboxPhotos, useSelection, type ChatPhoto,
} from './conversation';

/** A row of the history: a message the server has, or one still going out. */
type Row = { id: string; sentAt: string; sent?: MessageView; sending?: OutboxMessage };

/**
 * The chat with the driver, open only while the ride is — a port of the phone's
 * `chat_screen.dart`: the driver's face and name over it, the order's number under
 * the name (what to say when ringing about this ride), the phone at the far end,
 * and messages that carry photographs and voice.
 */
export function ChatScreen() {
  const { rideId = '' } = useParams();
  const { rides, loading } = useSession();
  const ride = rides.find((one) => one.id === rideId);
  if (!ride) return loading ? <Spinner /> : <Navigate to="/" replace />;
  return <RideChat key={ride.id} rideId={ride.id} />;
}

function RideChat({ rideId }: { rideId: string }) {
  const { api, me, rides, chatSignal, reading } = useSession();
  const ride = rides.find((one) => one.id === rideId)!;
  // A предзаказ's chat is opened over «Поездки», and goes back there.
  const { pathname } = useLocation();
  const back = useBack(pathname.startsWith('/orders') ? '/orders' : '/');
  const driver = ride.driver;
  const face = useAvatar(driver?.avatarKey);
  const [messages, setMessages] = useState<MessageView[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  useErrorHaptic(error?.message);
  const [failed, setFailed] = useState<OutboxMessage | null>(null);
  const [asking, setAsking] = useState<{ ids: string[]; done: (gone: boolean) => void } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ApiError | null>(null);
  const pending = usePending('ride', rideId);

  const load = useCallback(async () => {
    try {
      setMessages(await getMessages(api, rideId));
      setError(null);
    } catch (problem) {
      if (problem instanceof ApiError) setError(problem);
    }
  }, [api, rideId]);

  // What the driver writes arrives on the socket and is fetched at once; a poll
  // behind it, while the chat is looked at, for a socket that has quietly died.
  useEffect(() => {
    void load();
  }, [load, chatSignal]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [load]);
  // A message of ours that has landed is fetched back with its real id and the
  // server's time, and takes the place of the one still going out.
  useEffect(
    () =>
      onDelivered((delivery) => {
        if (delivery.kind === 'ride' && delivery.conversation === rideId) void load();
      }),
    [load, rideId],
  );

  // On screen: nothing is notified about this chat while it is being read, and what
  // the driver wrote is read once it has been in front of somebody — not in a tab
  // behind another one, and again on coming back to it. Opening the chat is also the
  // moment a browser will put the question of notifications.
  useEffect(() => {
    askToNotify();
    const read = () => {
      if (document.visibilityState !== 'visible') return;
      reading(chatTag(rideId));
      void markRead(api, rideId).catch(() => {});
    };
    read();
    document.addEventListener('visibilitychange', read);
    return () => {
      document.removeEventListener('visibilitychange', read);
      reading(null);
    };
  }, [api, rideId, reading, messages?.length]);

  const rows = useMemo<Row[]>(
    () => [
      ...(messages ?? []).map((message) => ({ id: message.id, sentAt: message.sentAt, sent: message })),
      ...pending.map((message) => ({ id: message.id, sentAt: message.sentAt, sending: message })),
    ],
    [messages, pending],
  );
  const pendingIds = new Set(pending.map((message) => message.id));

  const selection = useSelection(
    rows.map((row) => row.id),
    (ids) =>
      new Promise((done) => {
        setDeleteError(null);
        setAsking({ ids, done });
      }),
  );

  const photosOf = (message: MessageView): ChatPhoto[] =>
    message.storageKey && message.kind !== 'VOICE'
      ? [
          {
            id: message.id,
            author: message.senderId === me?.id ? 'Вы' : (driver?.name ?? 'Водитель'),
            sentAt: message.sentAt,
            caption: message.text,
            load: () => getChatAttachment(api, rideId, message.id),
          },
        ]
      : [];

  const gallery = rows.flatMap((row) => (row.sent ? photosOf(row.sent) : outboxPhotos(row.sending!)));

  return (
    <ChatLayout
      title={driver?.name ?? 'Водитель'}
      subtitle={`Заказ № ${ride.number}`}
      avatar={driver ? <Initials text={initials(driver.name)} size={36} src={face} /> : undefined}
      action={
        driver && (
          <a className="icon-btn" href={`tel:${driver.phone}`} aria-label="Позвонить водителю" title="Позвонить водителю">
            <Icon name="call_outlined" size={24} />
          </a>
        )
      }
      selection={selection}
      onBack={back}
    >
      {messages === null && !error ? (
        <Spinner />
      ) : (
        <ChatGallery photos={gallery} send={(file) => send('ride', rideId, '', [file])}>
          <ChatHistory
            rows={rows}
            id={(row) => row.id}
            sentAt={(row) => row.sentAt}
            // A message that has landed is not one that has gone: the server's copy
            // takes its place without the bubble flickering out.
            departs={(row) => row.sending === undefined}
            empty={
              <p className="body chat-invitation">
                Напишите водителю — например, у какого подъезда вас ждать.
              </p>
            }
            render={(row) => {
              const mine = row.sending !== undefined || row.sent!.senderId === me?.id;
              return (
                <Selectable id={row.id} mine={mine} selection={selection}>
                  {row.sending ? (
                    <OutboxBubble message={row.sending} onFailedTap={() => setFailed(row.sending!)} />
                  ) : (
                    <Bubble
                      mine={mine}
                      text={row.sent!.text}
                      sentAt={row.sent!.sentAt}
                      photos={photosOf(row.sent!)}
                      voices={
                        row.sent!.storageKey && row.sent!.kind === 'VOICE'
                          ? [{ id: row.sent!.id, load: () => getChatAttachment(api, rideId, row.sent!.id) }]
                          : []
                      }
                    />
                  )}
                </Selectable>
              );
            }}
          />
        </ChatGallery>
      )}
      {error && <div className="chat-error">{error.message}</div>}
      <Composer onSend={(text, files, voice) => send('ride', rideId, text, files, voice)} />

      {failed && (
        <FailedSheet
          onRetry={() => {
            retry(failed.id);
            setFailed(null);
          }}
          onDiscard={() => {
            discard(failed.id);
            setFailed(null);
          }}
          onClose={() => setFailed(null)}
        />
      )}
      {asking && (
        <DeleteMessagesSheet
          count={asking.ids.length}
          otherPerson="у водителя"
          anySent={asking.ids.some((id) => !pendingIds.has(id))}
          busy={deleting}
          error={deleteError}
          onClose={() => {
            asking.done(false);
            setAsking(null);
          }}
          onDelete={(forEveryone) => {
            // One still in the outbox is simply given up on; only what the server
            // has is asked of the server.
            asking.ids.filter((id) => pendingIds.has(id)).forEach(discard);
            const sent = asking.ids.filter((id) => !pendingIds.has(id));
            setDeleting(true);
            void (sent.length === 0 ? Promise.resolve() : deleteMessages(api, rideId, sent, forEveryone))
              .then(async () => {
                setMessages((current) => (current ?? []).filter((message) => !sent.includes(message.id)));
                asking.done(true);
                setAsking(null);
                await load();
              })
              .catch((problem: unknown) => {
                setDeleteError(
                  problem instanceof ApiError
                    ? problem
                    : new ApiError('unknown', 'Не удалось удалить сообщения. Попробуйте ещё раз.'),
                );
              })
              .finally(() => setDeleting(false));
          }}
        />
      )}
    </ChatLayout>
  );
}
